import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef } from "react";

import { useAuth0, useMemberLogin } from "@/integrations/fc/auth0";

export const Route = createFileRoute("/auth")({
  staticData: { sitemap: false },
  ssr: false,
  head: () => ({
    meta: [
      { title: "Member Login — Foreign Council On Economic Relations" },
      {
        name: "description",
        content:
          "Sign in to the members area of the Foreign Council On Economic Relations to access meeting documentation and invitations.",
      },
      { property: "og:title", content: "Member Login — Foreign Council" },
      {
        property: "og:description",
        content: "Access meeting documentation and invitations in the members area.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AuthPage,
});

// Sign-in return point. Opening it directly starts sign-in at once; returning
// from sign-in is handled by the provider, which sends the user back to the
// page they came from. Only an error leaves the user on this page.
function AuthPage() {
  const { isLoading, isAuthenticated, error } = useAuth0();
  const login = useMemberLogin();
  const navigate = useNavigate();
  const started = useRef(false);

  useEffect(() => {
    if (isLoading || error || started.current) return;
    const params = new URLSearchParams(window.location.search);
    if (params.has("code") || params.has("state")) return;
    started.current = true;
    if (isAuthenticated) void navigate({ to: "/members", replace: true });
    else login("/members");
  }, [isLoading, isAuthenticated, error, login, navigate]);

  return (
    <section className="bg-surface">
      <div className="mx-auto w-full max-w-[520px] px-5 py-16 lg:py-24">
        <h1 className="fc-h2 fc-rule text-foreground">Member Login</h1>
        <div className="mt-10 space-y-6 bg-card p-6 lg:p-8">
          {error ? (
            <>
              <p role="alert" className="text-sm text-destructive">
                Sign-in was not possible. Please try again or contact the secretariat.
              </p>
              <button
                type="button"
                onClick={() => login("/members")}
                className="w-full bg-primary px-7 py-3 text-base font-medium text-primary-foreground transition-opacity hover:opacity-85"
              >
                Try again
              </button>
              <p className="text-sm text-muted-foreground">
                If you require access, please contact the secretariat via the{" "}
                <Link to="/contact" className="underline">
                  contact page
                </Link>
                .
              </p>
            </>
          ) : (
            <p className="fc-body text-muted-foreground" aria-live="polite">
              Opening sign-in…
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
